import { makeAutoObservable, observable, runInAction } from 'mobx';
import i18n from '../i18n';
import { createClientId, type ChatMessage } from '../components/chat';
import type {
  EmailAccount,
  EmailConversation,
  EmailFilter,
  EmailService,
  SendNewEmailPayload,
} from '../services/emailService';
import { createMockEmailService } from '../services/mockEmailService';

type EmailThread = { messages: ChatMessage[]; loading: boolean };

const emptyThread: EmailThread = { messages: [], loading: false };

export const EMAIL_FILTERS: EmailFilter[] = ['all', 'unread', 'pending', 'intent', 'bounced'];

function matchesFilter(conversation: EmailConversation, filter: EmailFilter) {
  switch (filter) {
    case 'unread':
      return conversation.unreadCount > 0;
    case 'pending':
      return !conversation.bounced && conversation.lastMessageDirection === 'in';
    case 'intent':
      return conversation.intent;
    case 'bounced':
      return conversation.bounced;
    default:
      return true;
  }
}

/** Same immutable-update approach as MessageStore so chat components see new references. */
export default class EmailStore {
  conversations: EmailConversation[] = [];
  account: EmailAccount | null = null;
  loading = false;
  filter: EmailFilter = 'all';
  keyword = '';
  activeId: string | undefined = undefined;
  threads: Record<string, EmailThread> = {};

  private service: EmailService;

  constructor(service: EmailService = createMockEmailService()) {
    this.service = service;
    makeAutoObservable<EmailStore, 'service'>(
      this,
      {
        conversations: observable.ref,
        account: observable.ref,
        threads: observable.shallow,
        service: false,
      },
      { autoBind: true },
    );
  }

  get visibleConversations() {
    const q = this.keyword.trim().toLowerCase();
    return this.conversations
      .filter((item) => matchesFilter(item, this.filter))
      .filter(
        (item) =>
          !q ||
          item.peer.name.toLowerCase().includes(q) ||
          item.handle.toLowerCase().includes(q) ||
          item.email.toLowerCase().includes(q),
      )
      .slice()
      .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
  }

  get stats() {
    const unreadThreads = this.conversations.filter((item) => item.unreadCount > 0);
    return {
      unreadTotal: unreadThreads.reduce((sum, item) => sum + item.unreadCount, 0),
      unreadThreads: unreadThreads.length,
      pending: this.conversations.filter((item) => matchesFilter(item, 'pending')).length,
      intent: this.conversations.filter((item) => item.intent).length,
      sent: this.conversations.reduce((sum, item) => sum + item.sentCount, 0),
      received: this.conversations.reduce((sum, item) => sum + item.receivedCount, 0),
    };
  }

  get filterCounts(): Record<EmailFilter, number> {
    const counts = {} as Record<EmailFilter, number>;
    EMAIL_FILTERS.forEach((filter) => {
      counts[filter] = this.conversations.filter((item) => matchesFilter(item, filter)).length;
    });
    return counts;
  }

  get activeConversation() {
    return this.conversations.find((item) => item.id === this.activeId);
  }

  get activeThread(): EmailThread {
    return (this.activeId && this.threads[this.activeId]) || emptyThread;
  }

  get sendBlockedReason(): string | undefined {
    const conversation = this.activeConversation;
    if (conversation?.bounced) {
      return i18n.t('email.blocked_bounced');
    }
    if (this.account && this.account.monthlySent >= this.account.monthlyLimit) {
      return i18n.t('email.blocked_quota', { limit: this.account.monthlyLimit.toLocaleString() });
    }
    return undefined;
  }

  setFilter(filter: EmailFilter) {
    this.filter = filter;
  }

  setKeyword(keyword: string) {
    this.keyword = keyword;
  }

  async load() {
    this.loading = true;
    try {
      const [conversations, account] = await Promise.all([
        this.service.listConversations(),
        this.service.getAccount(),
      ]);
      runInAction(() => {
        this.conversations = conversations;
        this.account = account;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async select(id: string) {
    this.activeId = id;
    if (this.threads[id]) {
      return;
    }
    this.threads[id] = { messages: [], loading: true };
    try {
      const messages = await this.service.listMessages(id);
      runInAction(() => {
        this.threads[id] = { messages, loading: false };
      });
    } catch (e) {
      runInAction(() => {
        delete this.threads[id];
      });
      throw e;
    }
  }

  async markRead(id: string) {
    this.patchConversation(id, { unreadCount: 0 });
    await this.service.markRead(id);
  }

  async setIntent(id: string, intent: boolean) {
    this.patchConversation(id, { intent });
    await this.service.setIntent(id, intent);
  }

  async reply(content: string) {
    const id = this.activeId;
    const conversation = this.activeConversation;
    if (!id || !conversation || this.sendBlockedReason) {
      return;
    }
    const clientId = createClientId();
    const pending: ChatMessage = {
      id: clientId,
      clientId,
      direction: 'out',
      contentType: 'text',
      subject: `Re: ${conversation.subject}`,
      content,
      createdAt: new Date().toISOString(),
      status: 'sending',
    };
    this.setMessages(id, [...this.activeThread.messages, pending]);
    this.patchConversation(id, {
      lastMessagePreview: content,
      lastMessageAt: pending.createdAt,
      lastMessageDirection: 'out',
      unreadCount: 0,
      sentCount: conversation.sentCount + 1,
    });
    await this.deliver(id, pending);
  }

  async resend(message: ChatMessage) {
    const id = this.activeId;
    if (!id || message.status !== 'failed' || !message.clientId || this.sendBlockedReason) {
      return;
    }
    const retry: ChatMessage = { ...message, status: 'sending' };
    this.replaceMessage(id, message.clientId, retry);
    await this.deliver(id, retry);
  }

  async sendNew(payload: SendNewEmailPayload) {
    const result = await this.service.sendNew(payload);
    const [conversations, account] = await Promise.all([
      this.service.listConversations(),
      this.service.getAccount(),
    ]);
    runInAction(() => {
      this.conversations = conversations;
      this.account = account;
      this.threads = {};
    });
    if (this.activeId) {
      await this.select(this.activeId);
    }
    return result;
  }

  async bindAddress(address: string) {
    await this.service.bindAddress(address);
    runInAction(() => {
      if (this.account) {
        this.account = { ...this.account, boundAddress: address };
      }
    });
  }

  syncTalents() {
    return this.service.syncTalents();
  }

  private async deliver(id: string, pending: ChatMessage) {
    const clientId = pending.clientId as string;
    try {
      const saved = await this.service.sendReply({
        conversationId: id,
        clientId,
        content: pending.content,
      });
      runInAction(() => {
        this.replaceMessage(id, clientId, { ...saved, clientId });
        if (this.account) {
          this.account = { ...this.account, monthlySent: this.account.monthlySent + 1 };
        }
      });
    } catch {
      runInAction(() => {
        this.replaceMessage(id, clientId, { ...pending, status: 'failed' });
      });
    }
  }

  private setMessages(id: string, messages: ChatMessage[]) {
    this.threads[id] = { ...(this.threads[id] ?? emptyThread), messages };
  }

  private replaceMessage(id: string, clientId: string, next: ChatMessage) {
    const thread = this.threads[id];
    if (thread) {
      this.setMessages(
        id,
        thread.messages.map((item) => (item.clientId === clientId ? next : item)),
      );
    }
  }

  private patchConversation(id: string, patch: Partial<EmailConversation>) {
    this.conversations = this.conversations.map((item) =>
      item.id === id ? { ...item, ...patch } : item,
    );
  }
}
